import { RecipeFlags, RecipeModeTab, RecipeSchemaContext } from './types';

/**
 * Recipes whose card opens on a mode selector instead of the legacy `autoPick`
 * boolean. Each recipe defines its own modes; the first entry is the default.
 */
const MODE_TABS_BY_SLUG: Record<string, RecipeModeTab[]> = {
    'product-closeup': [
        { value: 'preset', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_PRESET', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_PRESET_HINT' },
        // Single Piece had presets and nothing else, so a vendor who wanted
        // "on a wet slate slab, raking light" had nowhere to say it.
        { value: 'prompt', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_PROMPT', labelFallback: 'Prompt', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_SCENE_PROMPT_HINT', hintFallback: 'Describe the shot in your own words' },
        { value: 'options', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_CUSTOM', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_CUSTOM_HINT' },
    ],
    // '3d-rotation' is wired end-to-end (widget, seed, worker pipeline) but
    // hidden from the menu pending better Flux Klein edit results. It stays a
    // valid `mode` value — the validation rules still handle it.
    'style-variation': [
        { value: 'prompt', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_PROMPT', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_PROMPT_HINT' },
        { value: 'change-angle', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_CHANGE_ANGLE', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_CHANGE_ANGLE_HINT' },
        { value: 'close-up', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_CLOSE_UP', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_CLOSE_UP_HINT' },
    ],
    // Presets only move the camera over a frozen scene, so "a hand unrolls
    // the rug" had nowhere to go. The Prompt tab lets the scene itself move.
    'image-to-video': [
        { value: 'preset', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_PRESET', labelFallback: 'Presets', hintKey: 'CONTENT_STUDIO.VIDEO_MODE_PRESET_HINT', hintFallback: 'Camera moves over a still scene' },
        { value: 'prompt', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_PROMPT', labelFallback: 'Prompt', hintKey: 'CONTENT_STUDIO.VIDEO_MODE_PROMPT_HINT', hintFallback: 'Describe what happens in your own words' },
    ],
};

/**
 * Single Piece · surface — a chosen look, or your own words.
 *
 * Two ways to answer one question, so they are tabs rather than a page: a
 * preset IS a written shot, so showing the brief beside it would offer the
 * vendor two answers at once and give the model both.
 *
 * No third tab. The old "Custom" assembled a shot out of six enumerations —
 * backdrop, framing, lighting, shadow, depth of field, camera height — and
 * could not reach "held up in a café, shot on a phone" however they were
 * combined. Anything that list could say, a sentence says better.
 */
const SURFACE_SINGLE_TABS: RecipeModeTab[] = [
    { value: 'preset', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_PRESET', labelFallback: 'Presets', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_PRESET_HINT', hintFallback: 'A hand-tuned editorial look' },
    { value: 'prompt', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_PROMPT', labelFallback: 'Prompt', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_SCENE_PROMPT_HINT', hintFallback: 'Describe the shot in your own words' },
];

/** Furniture Lifestyle Scene — same shape as Product Photography. */
const LIFESTYLE_FURNITURE_TABS: RecipeModeTab[] = [
    { value: 'prompt', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_PROMPT', labelFallback: 'Prompt', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_SCENE_PROMPT_HINT', hintFallback: 'Describe the scene in your own words' },
    { value: 'options', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_OPTIONS', labelFallback: 'Options', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_OPTIONS_HINT', hintFallback: 'Pick the environment, style & lighting' },
];

/** Room Scene · In-Room (studio ON). */
const ROOM_SCENE_STUDIO_TABS: RecipeModeTab[] = [
    { value: 'prompt', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_PROMPT', labelFallback: 'Prompt', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_SCENE_PROMPT_HINT', hintFallback: 'Describe the scene in your own words' },
    { value: 'options', labelKey: 'CONTENT_STUDIO.RECIPE_MODE_CUSTOM', hintKey: 'CONTENT_STUDIO.RECIPE_MODE_CUSTOM_HINT' },
];

/** Extra mode tab sets registered by other recipes (style-variation etc.). */
export function registerModeTabs(slug: string, tabs: RecipeModeTab[]): void {
    MODE_TABS_BY_SLUG[slug] = tabs;
}

const isTrue = (v: any) => v === true || v === 'true';
const isFalse = (v: any) => v === false || v === 'false';

/**
 * Resolve the derived state the rest of the rules depend on. Order matters:
 * the mode tabs depend on the Room Scene flags, and `autoPickOn` depends on
 * the resolved mode.
 */
export function resolveRecipeFlags(ctx: RecipeSchemaContext): RecipeFlags {
    const { recipe, inputs, productVisualizationType: vt } = ctx;
    const slug = recipe.slug;

    const surfaceMode = (inputs.surfaceMode || 'applied') as 'single' | 'applied';
    const isSurfaceApplied = slug === 'product-closeup' && vt === 'surface' && surfaceMode === 'applied';
    /**
     * Single Piece · surface — one page, no tabs.
     *
     * The card asks two questions now, and they are not alternatives: how the
     * piece is POSED, and what SCENE it is in. Tabs made them look like a
     * choice between a curated look and writing your own, when in truth the
     * nine curated looks were nine backgrounds for the same unchanging pose.
     */
    const isSurfaceSingle = slug === 'product-closeup' && vt === 'surface' && surfaceMode === 'single';

    const use3dOn = isTrue(inputs.use3dCamera);
    const roomSceneStudio = isSurfaceApplied && use3dOn;
    /**
     * A Room Scene that has not switched the studio on yet.
     *
     * This used to be a MODE — "Lifestyle" — with its own fields, its own
     * standalone Use-AI toggle and its own worker pipeline behind
     * lifestyle-scene. That mode is gone: the studio is how a Room Scene is
     * framed, new cards open with it on, and the toggle only ever switches it
     * on. What remains is the transient state of a card saved before that, or
     * one a plan set `use3dCamera: false` on — it still renders, it just has
     * nothing special about it any more.
     */
    const roomSceneUnposed = isSurfaceApplied && !use3dOn;

    const isFromRefSurface = slug === 'from-reference' && vt === 'surface';
    /**
     * Keep the vendor's photograph and change only the surface in it.
     *
     * The one route through this recipe that poses nothing: the reference IS
     * the shot, so its floor is segmented and the real seamless is mapped onto
     * the detected plane in perspective, with the photograph's own light
     * carried back over it. Nothing is generated, so nothing can drift.
     *
     * `matchStrength` has always meant this — "exact copy (same composition,
     * lighting, colors)" — but the studio was made mandatory everywhere it
     * existed, which forced `use3dCamera` on and left this branch unreachable.
     * A rule about where the studio is useful was applied where it is not: an
     * exact copy has no camera to choose.
     */
    const fromRefExact = isFromRefSurface && String(inputs.matchStrength || 'exact') === 'exact';
    /**
     * The same room, refurnished.
     *
     * Maps the surface exactly as the exact copy does, then generates a new
     * scene around it from the reference's depth — so like the exact copy it
     * has no camera to choose, and unlike it the room's contents change.
     */
    const fromRefSameStructure = isFromRefSurface && String(inputs.matchStrength) === 'inspired';
    const fromRefStudio = isFromRefSurface && use3dOn && !fromRefExact && !fromRefSameStructure;

    /**
     * The material is installed and shot at the chosen angle, but no room is
     * invented around it.
     *
     * A real, distinct shot — the floor close-up most reference photographs
     * are — and not the same thing as Single Piece, where nothing is installed
     * at all. It was briefly removed as a contradiction; the contradiction was
     * only ever in the old label, which never said the material stays
     * installed either way.
     */
    const appliedProductOnly = roomSceneStudio && isFalse(inputs.appliedEnvironment);

    // Every Room Scene gets the studio tabs now. The tab-less branch belonged
    // to Room Scene · Lifestyle, which no longer exists.
    const modeTabs: RecipeModeTab[] | undefined =
        isSurfaceSingle ? SURFACE_SINGLE_TABS
        : slug === 'lifestyle-scene' && vt === 'model3d' ? LIFESTYLE_FURNITURE_TABS
        : isSurfaceApplied ? ROOM_SCENE_STUDIO_TABS
        : MODE_TABS_BY_SLUG[slug];

    const modeDefault = modeTabs?.[0]?.value;
    const hasMode = !!modeTabs;
    // Nodes saved while "Use AI" was a tab still carry mode: 'ai'. It is not a
    // tab any more, so they read as the settings mode — the same fields the AI
    // was filling in for them, now visible.
    /**
     * A node that already has a brief belongs to the Prompt tab.
     *
     * Presets are the friendlier landing for a vendor adding a card by hand —
     * a whole shot, no typing — so they are the first tab and therefore the
     * default. But Single Piece spent a while as a brief-only card, and every
     * node written then carries `sceneDetails` and no `mode`. Landing those on
     * Presets would show a curated look and quietly ignore what they wrote.
     */
    const inferredMode = isSurfaceSingle && !inputs.mode
        && typeof inputs.sceneDetails === 'string' && inputs.sceneDetails.trim()
        ? 'prompt'
        : inputs.mode;
    const savedMode = inferredMode === 'ai' ? 'options' : inferredMode;
    const modeValue = hasMode
        ? (modeTabs!.some((tab) => tab.value === savedMode) ? savedMode : (modeDefault as string))
        : '';

    // Image to Video gained tabs after its "Use AI" flag shipped; its Presets
    // tab still auto-picks when a node asks to, so the card must say so.
    const autoPickOn = (slug === 'product-closeup' || (slug === 'lifestyle-scene' && hasMode)
        || (slug === 'image-to-video' && modeValue === 'preset'))
        // The AI tab is gone; a node keeps auto-picking only if it was
        // explicitly asked to, so re-running an old node does what it did.
        ? isTrue(inputs.autoPick)
        // Any other tabbed recipe (style-variation) has no AI mode at all; the
        // legacy boolean only applies to recipes without tabs.
        : hasMode ? false : isTrue(inputs.autoPick);

    return {
        surfaceMode,
        isSurfaceApplied,
        isSurfaceSingle,
        use3dOn,
        roomSceneStudio,
        roomSceneUnposed,
        isFromRefSurface,
        fromRefExact,
        fromRefSameStructure,
        fromRefStudio,
        appliedProductOnly,
        autoPickOn,
        hasMode,
        modeValue,
        modeDefault,
        modeTabs,
    };
}

/**
 * Slug the generation is actually submitted under.
 *
 * Room Scene used to be one card with two methods behind the 3D-studio toggle:
 * 3D ON rendered through product-closeup, 3D OFF submitted under lifestyle-scene
 * and went down the depth / visualizer pipeline. That second method is gone.
 * The studio is how a Room Scene is framed, the toggle only ever switches it ON,
 * and a surface product can no longer reach lifestyle-scene at all — it is not
 * in the picker for surface either. Furniture keeps Lifestyle Scene as its own
 * card, which is why the recipe still exists.
 *
 * Nothing is remapped any more, so this is the identity — kept because callers
 * pass a bare slug through it and one place is where a future split would go.
 */
export function resolveSubmitSlug(ctx: {
    /** Only the slug is consulted, so callers holding a bare slug can use this too. */
    recipe: { slug: string };
    inputs?: Record<string, any>;
    productVisualizationType?: string;
}): string {
    return ctx.recipe.slug;
}

/**
 * Pad the inputs when a card submits under a DIFFERENT recipe than its own.
 *
 * The backend validates against the submitted recipe and does not filter by
 * `appliesTo`, so a card routed elsewhere has to carry that recipe's required
 * fields. Nothing routes elsewhere today — `resolveSubmitSlug` is the identity
 * since Room Scene · Lifestyle was removed — so this returns the inputs
 * untouched, and stays for the same reason that function does.
 */
export function fillSubmitDefaults(
    ctx: Pick<RecipeSchemaContext, 'inputs' | 'productVisualizationType' | 'relatedRecipes'> & {
        recipe: { slug: string };
    },
    inputs: Record<string, any>,
): Record<string, any> {
    const submitSlug = resolveSubmitSlug(ctx);
    if (submitSlug === ctx.recipe.slug) return inputs;

    const target = ctx.relatedRecipes?.[submitSlug];
    if (!target?.inputs) return inputs;

    const out = { ...inputs };
    for (const input of target.inputs) {
        if (input.required && input.default !== undefined && (out[input.key] == null || out[input.key] === '')) {
            out[input.key] = input.default;
        }
    }
    return out;
}

/**
 * The name the vendor sees for a node, where it differs from the recipe's own.
 *
 * Surface Product Photography is two cards in the picker — "Single Piece" and
 * "Room Scene" — told apart by `surfaceMode`. Anything showing the database
 * name instead is naming a recipe the vendor cannot find in their own UI, which
 * has now happened in the agent's replies, in `list_recipes` and on the plan
 * card. Returns undefined when the recipe's own name is the right one.
 */
export function surfaceShotName(
    slug: string,
    inputs: Record<string, any> | undefined,
    visualizationType?: string,
): string | undefined {
    if (slug !== 'product-closeup' || visualizationType !== 'surface') return undefined;
    return (inputs?.surfaceMode || 'applied') === 'applied' ? 'Room Scene' : 'Single Piece';
}
